import type { MessagePayload } from 'firebase/messaging'
import { useMutation } from '@vue/apollo-composable'
import { getToken, onMessage } from 'firebase/messaging'
import gql from 'graphql-tag'
import { onMounted, ref } from 'vue'
import { useFirebase } from './useFirebase'

interface Notification {
  title: string
  body: string
  data?: Record<string, any>
}

export function usePushNotifications() {
  const { initializeFirebase, messaging, vapidKey } = useFirebase()
  const fcmToken = ref<string | null>(null)
  const notificationPermission = ref<NotificationPermission | null>(null)
  const notificationPayload = ref<MessagePayload | null>(null)
  const notifications = ref<Notification[]>([])
  const isSupported = ref(false)
  const isGlobalSubscribed = ref(false)

  // Subscribe to global notifications using backend mutation
  const { mutate: subscribeToGlobal } = useMutation(gql`
    mutation SubscribeToGlobal($token: String!) {
      subscribeToGlobal(token: $token)
    }
  `)

  // Register user token
  const { mutate: registerToken } = useMutation(gql`
    mutation RegisterUserToken($userId: String!, $token: String!, $deviceInfo: String) {
      registerUserToken(userId: $userId, token: $token, deviceInfo: $deviceInfo)
    }
  `)

  // Send test notification
  const { mutate: sendTestPush } = useMutation(gql`
    mutation SendPushToken($title: String!, $body: String!, $tokens: [String!]) {
      sendPush(
        title: $title, 
        body: $body,
        tokens: $tokens
      )
    }
  `)

  // Check if push notifications are supported
  onMounted(() => {
    isSupported.value
      = typeof window !== 'undefined'
        && 'Notification' in window
        && 'serviceWorker' in navigator
  })

  // Register FCM token for a specific user
  const registerUserToken = async (userId: string, token: string): Promise<boolean> => {
    try {
      if (!token || token === 'local-only-mode') {
        console.warn('Невозможно зарегистрировать локальный токен')
        return false
      }

      const deviceInfo = typeof navigator !== 'undefined'
        ? `${navigator.userAgent} (${navigator.platform})`
        : undefined

      const result = await registerToken({
        userId,
        token,
        deviceInfo,
      })

      return !!result?.data?.registerUserToken
    }
    catch (error) {
      console.error('Ошибка при регистрации токена пользователя:', error)
      return false
    }
  }

  // Subscribe to global notifications using backend mutation
  const subscribeToGlobalNotifications = async (token: string): Promise<boolean> => {
    try {
      const result = await subscribeToGlobal({ token })
      isGlobalSubscribed.value = !!result?.data?.subscribeToGlobal
      return isGlobalSubscribed.value
    }
    catch (error) {
      console.error('Error subscribing to global notifications:', error)
      return false
    }
  }

  // Request notification permission
  const requestPermission = async () => {
    if (!isSupported.value) {
      console.warn('Push-уведомления не поддерживаются в этом браузере')
      return null
    }

    try {
      // Initialize Firebase if not already done
      initializeFirebase()

      if (!messaging.value) {
        console.error('Firebase messaging недоступен')
        return null
      }

      // Request permission from the user
      notificationPermission.value = await Notification.requestPermission()

      if (notificationPermission.value !== 'granted') {
        console.warn('Разрешение на уведомления не предоставлено')
        return null
      }

      try {
        // Get FCM token using the VAPID key
        const token = await getToken(messaging.value, {
          vapidKey,
        })

        fcmToken.value = token

        // Subscribe to global notifications if we have a token
        if (token) {
          await subscribeToGlobalNotifications(token)
        }

        return token
      }
      catch (networkError) {
        console.error('Ошибка сети при подключении к Firebase:', networkError)
        // Still consider permissions granted even if we couldn't get a token
        // This allows the app to function normally but without push notification capability
        return 'local-only-mode'
      }
    }
    catch (error) {
      console.error('Ошибка при запросе разрешения на уведомления:', error)
      return null
    }
  }

  // Register listener for foreground messages
  const setupForegroundListener = () => {
    if (!messaging.value) {
      console.warn('Firebase messaging не инициализирован')
      return
    }

    onMessage(messaging.value, (payload) => {
      notificationPayload.value = payload

      // Extract notification data
      if (payload.notification) {
        const newNotification: Notification = {
          title: payload.notification.title || '',
          body: payload.notification.body || '',
          data: payload.data,
        }

        notifications.value.push(newNotification)

        // Show browser notification if permission is granted
        if (notificationPermission.value === 'granted' && typeof window !== 'undefined') {
          // Use the notification API to display notifications
          const notification = {
            title: newNotification.title,
            options: {
              body: newNotification.body,
            },
          }

          // Only create notification if supported and permission granted
          if ('Notification' in window) {
            void navigator.serviceWorker.ready.then((registration) => {
              registration.showNotification(notification.title, notification.options)
            })
          }
        }
      }
    })
  }

  // Send FCM token to backend using GraphQL mutations
  const saveTokenToBackend = async (token: string): Promise<boolean> => {
    try {
      // First, subscribe to global notifications
      await subscribeToGlobalNotifications(token)

      // Send a test notification if needed
      /*
      await sendTestPush({
        title: "Test Notification",
        body: "Your push notifications are working!",
        tokens: [token]
      })
      */

      return true
    }
    catch (error) {
      console.error('Error saving token to backend:', error)
      return false
    }
  }

  // Send a test notification to a specific token
  const sendNotification = async (title: string, body: string, token: string): Promise<boolean> => {
    try {
      await sendTestPush({
        title,
        body,
        tokens: [token],
      })
      return true
    }
    catch (error) {
      console.error('Error sending notification:', error)
      return false
    }
  }

  return {
    fcmToken,
    notificationPermission,
    notificationPayload,
    notifications,
    isSupported,
    isGlobalSubscribed,
    requestPermission,
    setupForegroundListener,
    saveTokenToBackend,
    sendNotification,
    subscribeToGlobalNotifications,
    registerUserToken,
  }
}
